import { ref, computed, toValue, type MaybeRefOrGetter } from "vue";
import {useLocale} from "@/composables/useLocale.ts";

/**
 * A generic composable to filter a list of items based on a search query and specific object keys.
 * * @param items - A raw array, ref, or getter function returning an array of objects.
 * @param searchKeys - An array of keys belonging to the object type to search through.
 */
export function useSearch<T extends Record<string, any>>(
  items: MaybeRefOrGetter<T[]>,
  searchKeys: MaybeRefOrGetter<Array<keyof T>>
) {
    const { tUseCase } = useLocale()
    const searchQuery = ref("");

    const filteredItems = computed(() => {
        // 1. Unwraps refs/getters safely using toValue
        const list = toValue(items);
        const keys = toValue(searchKeys);
        const query = searchQuery.value.trim().toLowerCase();

        // 2. If the query is empty, return the whole list immediately
        if (!query) return list;

        // 3. Filter the list
        return list.filter((item) => {
            return keys.some((key) => {
                const value = typeof item[key] === 'string' ? item[key] : tUseCase(item[key]); // or maybe check of locales?

                // Ensure the value exists and convert to string for the inclusion check
                if (value !== null && value !== undefined) {
                    return String(value).toLowerCase().includes(query);
                }
                return false;
            });
        });
    });

    return {
        searchQuery,
        filteredItems,
    };
}